import React, { useRef } from 'react';
import Image from '../Image';
import { ArrowDownToLine } from 'lucide-react';
import PhoneNumber from '../shared/PhoneNumber';
// import { toPng } from 'html-to-image';
// import jsPDF from 'jspdf';

interface SocialKit {
  social_type: string;
  link_url: string;
}

interface ProfileCardProps {
  user: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    cell_phone?: string;
    country_code?: string;
    croppedAvatarUrl: string;
    gender?: string;
    height_ft?: number;
    height_in?: number;
    height?: number;
    weight_lbs?: number;
    weight?: number;
    measure_system?: 'imperial' | 'metric';
    city?: string;
    state?: string;
    country?: string;
    subscription_tier?: string;
    pro_or_higher?: boolean;
    social_kits?: SocialKit[];
  };
}

export default function ProfileCard({ user }: ProfileCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  const isPro = user.pro_or_higher;

  const handleDownload = async () => {
    if (!cardRef.current) return;

    try {
      // Hide download button for capture
      // const btn = cardRef.current.querySelector('.download-btn') as HTMLElement;
      // if (btn) btn.style.display = 'none';

      // const dataUrl = await toPng(cardRef.current, {
      //   cacheBust: true,
      //   quality: 0.95,
      //   backgroundColor: '#ffffff',
      //   width: 1080, // Force a fixed width for capture
      //   style: {
      //     width: '1080px', // Ensure CSS matches
      //     maxWidth: 'none', // Override any max-width constraints
      //     margin: '0', // Reset margins for capture
      //     transform: 'none' // Reset any transforms
      //   }
      // });

      // // Restore button
      // if (btn) btn.style.display = '';

      // // Load image to get true dimensions
      // const img = new window.Image();
      // img.src = dataUrl;
      // await new Promise((resolve) => { img.onload = resolve; });

      // // Calculate dimensions to fit A4
      // const pdf = new jsPDF('p', 'mm', 'a4');
      // const pdfWidth = pdf.internal.pageSize.getWidth();
      // const pdfHeight = pdf.internal.pageSize.getHeight();

      // const imgWidth = img.naturalWidth;
      // const imgHeight = img.naturalHeight;
      // const ratio = imgWidth / imgHeight;

      // // Fit to page with margins
      // const margin = 15; // 15mm margin for safety
      // const maxPdfWidth = pdfWidth - (margin * 2);
      // const maxPdfHeight = pdfHeight - (margin * 2);

      // let finalPdfWidth = maxPdfWidth;
      // let finalPdfHeight = finalPdfWidth / ratio;

      // // If height is too big, scale by height instead
      // if (finalPdfHeight > maxPdfHeight) {
      //   finalPdfHeight = maxPdfHeight;
      //   finalPdfWidth = finalPdfHeight * ratio;
      // }

      // const x = (pdfWidth - finalPdfWidth) / 2;
      // const y = margin; // Top align with margin

      // pdf.addImage(dataUrl, 'PNG', x, y, finalPdfWidth, finalPdfHeight);
      // pdf.save(`${(fullName || "").replace(/\s+/g, '_')}_Profile_Card.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Ensure button is restored even on error
      const btn = cardRef.current?.querySelector('.download-btn') as HTMLElement;
      if (btn) btn.style.display = '';
    }
  };

  // Format height
  const formatHeight = () => {
    if (user.measure_system === 'imperial') {
      if (user.height_ft && user.height_in) {
        return `${user.height_ft}'${user.height_in}"`;
      }
    } else {
      if (user.height) {
        return `${user.height} cm`;
      }
    }
    return '';
  };

  // Format weight
  const formatWeight = () => {
    if (user.measure_system === 'imperial') {
      if (user.weight_lbs) {
        return `${user.weight_lbs} lbs`;
      }
    } else {
      if (user.weight) {
        return `${user.weight} kg`;
      }
    }
    return '';
  };

  // Format location
  const formatLocation = () => {
    const parts = [];
    if (user.city) parts.push(user.city);
    if (user.state) parts.push(user.state);
    if (user.country) {
      const countryNames: { [key: string]: string } = {
        'US': 'United States',
        'CA': 'Canada',
        'GB': 'United Kingdom'
      };
      parts.push(countryNames[user.country] || user.country);
    }
    return parts.length > 0 ? parts.join(', ') : '';
  };

  // Get IMDb link
  const getImdbLink = () => {
    if (user.social_kits) {
      const imdbKit = user.social_kits.find(sk => sk.social_type === 'imdb');
      return imdbKit?.link_url || '';
    }
    return '';
  };

  // Get website link (could be from social_kits or another field)
  const getWebsiteLink = () => {
    // For now, we'll check if there's a website in social_kits
    // You may need to add a website field to the User model
    return '';
  };

  // Format phone numbers (assuming cell_phone might contain multiple numbers)
  const formatPhones = () => {
    if (!user.cell_phone) return [];
    // Split by common delimiters if multiple phones
    return user.cell_phone.split(/[,;|]/).map(p => p.trim()).filter(p => p);
  };

  const phones = formatPhones();
  const imdbLink = getImdbLink();
  const websiteLink = getWebsiteLink();

  return (
    <div ref={cardRef} className="w-full max-w-5xl mx-auto bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8 my-4 sm:my-6 md:my-8 relative">
      {/* Download Button */}
      <div className="flex justify-end mb-4 print:hidden download-btn">
        <button
          type="button"
          onClick={handleDownload}
          className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-500 text-white text-xs sm:text-sm font-medium rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors shadow-sm"
          title="Download"
        >
          <ArrowDownToLine className="w-4 h-4" />
          <span className="hidden sm:inline"></span>
        </button>
      </div>
      <div className="flex flex-col md:flex-row gap-4 sm:gap-6 md:gap-8 relative">
        {/* Profile Picture Section */}
        <div className="flex-shrink-0 mx-auto md:mx-0">
          <div className="w-32 h-40 sm:w-40 sm:h-52 md:w-48 md:h-64 rounded-xl overflow-hidden bg-gray-100">
            <Image
              src={user.croppedAvatarUrl || '/icons/default_avatar.svg'}
              alt={`${fullName} profile`}
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        {/* Main Content Section */}
        <div className="flex-1 flex flex-col">
          {/* Header Row with Name and FILMIK Box */}
          <div className=" border-b border-gray-200  flex flex-col md:flex-row md:items-start md:justify-between mb-4 gap-4">
            <div className="flex-1">
              {/* Header with Name, PRO Badge, and Heart */}
              <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{fullName}</h1>
                {isPro && (
                  <span className="bg-black text-white text-xs font-semibold px-2 py-1 rounded-full">
                    PRO
                  </span>
                )}
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* Profession */}
              <p className="text-gray-600 mb-4 text-sm sm:text-base"></p>
              {/* Location and Measurements */}
              <div className="space-y-2 mb-4 sm:mb-6">
                {formatLocation() && (
                  <div className="flex items-start sm:items-center gap-2 text-gray-700 text-sm sm:text-base">
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0 mt-0.5 sm:mt-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span className="break-words">{formatLocation()}</span>
                  </div>
                )}
                {(user.gender || formatHeight() || formatWeight()) && (
                  <div className="flex items-start sm:items-center gap-2 text-gray-700 text-sm sm:text-base">
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0 mt-0.5 sm:mt-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 13a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1v-6z"
                      />
                    </svg>
                    <span className="break-words">
                      {[
                        user.gender?.charAt(0).toUpperCase() + user.gender?.slice(1),
                        formatHeight(),
                        formatWeight()
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* FILMIK Promotional Box - Top Right on desktop, below on mobile */}
            <div className="bg-gray-100 rounded-lg p-3 sm:p-4 w-full md:w-64 flex-shrink-0 md:ml-4 h-full">
              <div className="flex items-center gap-2 mb-2">
                {/* FILMIK Logo with Text */}
                <div className="flex items-center gap-2">
                  <img
                    src="/assets/filmik-brandmark-black.svg"
                    alt="FILMIK"
                    className="h-6 sm:h-8 w-auto"
                  />
                  <span className="font-bold text-gray-900 text-sm sm:text-base">Filmik</span>
                </div>
              </div>
              <hr className="border-gray-300 mb-2" />
              <p className="text-xs sm:text-sm text-gray-700">Hair & Makeup Kit Cover Sheet</p>
              <p className="text-xs text-gray-600 mt-1">www.filmik.io</p>
            </div>
          </div>

       

          {/* Contact Information Grid */}
          <div className="border border-gray-200 p-4 sm:p-6 mt-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
              {/* Phone(s) */}
              <div
                className="flex flex-col gap-2 "
              >
                <div className="flex items-center gap-2 mb-1">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Phone(s)</span>
                </div>
                <div className="space-y-1">
                  {phones.length > 0 ? (
                    phones.map((phone, index) => (
                      <p key={index} className="text-xs sm:text-sm text-gray-600 break-words">
                        <PhoneNumber
                          phone={phone}
                          country={user.country}
                          country_code={user.country_code}
                          showIcon={false}
                        />
                      </p>
                    ))
                  ) : (
                    <div className="h-4 w-28 bg-gray-200 rounded-md animate-pulse mt-1 mb-1"></div>
                  )}
                </div>
              </div>

              {/* Email */}
              <div
                className="flex flex-col gap-2 "
              >
                <div className="flex items-center gap-2 mb-1">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Email</span>
                </div>
                {user.email ? (
                  <p className="text-xs sm:text-sm text-gray-600 break-all">{user.email}</p>
                ) : (
                  <div className="h-4 w-28 bg-gray-200 rounded-md animate-pulse mt-1 mb-1"></div>
                )}
              </div>

              {/* IMDb Profile */}
              <div
                className="flex flex-col gap-2 "
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs sm:text-sm font-medium text-gray-700">IMDb Profile</span>
                </div>
                {imdbLink ? (
                  <p className="text-xs sm:text-sm text-gray-600 break-words">
                    <a
                      href={imdbLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 break-all"
                    >
                      {imdbLink?.replace(/^https?:\/\//, '')}
                    </a>
                  </p>
                ) : (
                  <div className="h-4 w-28 bg-gray-200 rounded-md animate-pulse mt-1 mb-1"></div>
                )}
              </div>

              {/* Website */}
              <div
                className="flex flex-col gap-2 "
              >
                <div className="flex items-center gap-2 mb-1">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                    />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Website</span>
                </div>
                {websiteLink ? (
                  <p className="text-xs sm:text-sm text-gray-600 break-words">
                    <a
                      href={websiteLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 break-all"
                    >
                      {websiteLink?.replace(/^https?:\/\//, '')}
                    </a>
                  </p>
                ) : (
                  <div className="h-4 w-28 bg-gray-200 rounded-md animate-pulse mt-1 mb-1"></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
